import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { useIdleTimeout } from '../hooks/useIdleTimeout';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const resp = await authAPI.getProfile();
      setUser(resp.data);
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const resp = await authAPI.login(email, password);
    const { access_token, refresh_token, user: profile } = resp.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setUser(profile);
    return profile;
  };

  const loginWithMS365 = () => {
    authAPI.ms365Login();
  };

  const handleMS365Callback = (token, refresh) => {
    localStorage.setItem('access_token', token);
    localStorage.setItem('refresh_token', refresh);
    loadUser();
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  // Session idle timeout — log out after 10 min of inactivity
  const handleIdleTimeout = useCallback(() => {
    if (user) {
      logout();
    }
  }, [user]);
  useIdleTimeout(handleIdleTimeout);

  const isStaff = user?.user_type === 'STAFF';
  const isClient = user?.user_type === 'CLIENT';
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'CTO';

  const isSupportLead = user?.role === 'SUPP_LEAD';

  return (
    <AuthContext.Provider value={{
      user, loading, login, loginWithMS365, handleMS365Callback, logout,
      isStaff, isClient, isAdmin, isSupportLead,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
